/* ============================================================================
 * useContextValue.ts - Assembles the complete context value
 * ============================================================================ */

import type { ProjectDataContextType } from '../types';

type UseContextValueParams = Omit<ProjectDataContextType, 'setFilters'> & {
  instrumentedSetFilters: ProjectDataContextType['setFilters'];
};

/**
 * Hook that assembles the complete context value from all the individual hooks
 */
export function useContextValue({
  projectData,
  treeNodes,
  nodeMap,
  expandedNodeIds,
  selectedNodeIds,
  filteredNodes,
  applyOp,
  captureOperation,
  moveNodeTo,
  updateNode,
  batchUpdateNodes,
  moveGroup,
  deleteNode,
  deleteNodes,
  createNode,
  getNode,
  getVisibleNodes,
  toggleExpanded,
  selectNode,
  expandAll,
  collapseAll,
  getRenderableNodes,
  filters,
  instrumentedSetFilters,
  searchQuery,
  setSearchQuery,
  sortBy,
  setSortBy,
  isCompactView,
  setIsCompactView,
  undo,
  redo,
  canUndo,
  canRedo,
  clearHistory,
  jumpToHistoryPosition,
  getUndoHistoryList,
  undoState,
  isUndoRedoInProgress,
  importProjectData,
  updateProgress,
  wsState,
  syncState,
  reconnectWebSocket,
  retrySyncRecovery,
  checkServerVersion,
  syncWithServer,
  hasUnsavedChanges,
  localCacheSaveFailed = false,
  setHasUnsavedChanges,
  getSnapshotPerformanceReport,
  configureSnapshotHistory
}: UseContextValueParams): ProjectDataContextType {

  // Return a minimal context value when no project data is available
  // This allows views to render empty states without crashing
  if (!projectData) {
    return {
      projectData: null,
      treeNodes: [],
      nodeMap: new Map(),
      expandedNodeIds: new Set(),
      selectedNodeIds: new Set(),
      applyOp,
      captureOperation,
      moveNodeTo,
      updateNode,
      batchUpdateNodes,
      moveGroup,
      deleteNode,
      deleteNodes,
      createNode,
      getNode,
      getVisibleNodes,
      toggleExpanded,
      selectNode,
      expandAll,
      collapseAll,
      getRenderableNodes,
      filters,
      setFilters: instrumentedSetFilters,
      filteredNodes: [],
      searchQuery,
      setSearchQuery,
      sortBy,
      setSortBy,
      isCompactView,
      setIsCompactView,
      undo,
      redo,
      canUndo,
      canRedo,
      clearHistory,
      importProjectData,
      updateProgress,
      isUndoRedoInProgress,
      jumpToHistoryPosition,
      getUndoHistoryList,
      undoState,
      wsState,
      syncState,
      reconnectWebSocket,
      retrySyncRecovery,
      checkServerVersion,
      syncWithServer,
      hasUnsavedChanges,
      localCacheSaveFailed,
      setHasUnsavedChanges,
      getSnapshotPerformanceReport,
      configureSnapshotHistory
    };
  }

  return {
    projectData,
    treeNodes,
    nodeMap,
    expandedNodeIds,
    selectedNodeIds,
    applyOp,
    captureOperation,
    moveNodeTo,
    updateNode,
    batchUpdateNodes,
    moveGroup,
    deleteNode,
    deleteNodes,
    createNode,
    getNode,
    getVisibleNodes,
    toggleExpanded,
    selectNode,
    expandAll,
    collapseAll,
    getRenderableNodes,
    filters,
    setFilters: instrumentedSetFilters,
    filteredNodes,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    isCompactView,
    setIsCompactView,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
    importProjectData,
    updateProgress,
    isUndoRedoInProgress,
    jumpToHistoryPosition,
    getUndoHistoryList,
    undoState,
    wsState,
    syncState,
    reconnectWebSocket,
    retrySyncRecovery,
    checkServerVersion,
    syncWithServer,
    hasUnsavedChanges,
    localCacheSaveFailed,
    setHasUnsavedChanges,
    getSnapshotPerformanceReport,
    configureSnapshotHistory
  };
}
